import {Dropdown} from "primereact/dropdown";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {SEGMENT} from "@reva/shared/reva-spec";

import {selectHasOtherSegmentSource} from "../../redux/objectParametersSlice";
import {selectSegmentWritePending} from "../../redux/persistenceSlice";
import ParameterFieldContainer from "./ParameterFieldContainer";
import {getResourceColor} from "./paramHooks";
import SegmentConflictOptions from "./SegmentConflictOptions";

const availableSegments = [
    {value: SEGMENT.WHG_K, labelI18n: "object.segments.WHG_K"},
    {value: SEGMENT.EZFH_K, labelI18n: "object.segments.EZFH_K"},
    {value: SEGMENT.MFHWGH_K, labelI18n: "object.segments.MFHWGH_K"},
    {value: SEGMENT.GRST_K, labelI18n: "object.segments.GRST_K"},
    {value: SEGMENT.WHG_M, labelI18n: "object.segments.WHG_M"},
];

export default function ParameterInputSegment({label}) {
    const {t} = useTranslation();
    // Zurücksetzen nimmt die Konfliktauflösung zurück -- das geht nur, wenn eine andere Quelle
    // einspringen kann. Ohne sie bliebe der technische Anker, den niemand gewählt hat (#19444).
    const hasOtherSegmentSource = useSelector(selectHasOtherSegmentSource);
    // Der gewählte Wert steht sofort im Control, geschrieben ist er aber erst nach dem Rückläufer --
    // ohne Anzeige sieht ein laufender Wechsel deshalb bereits fertig aus. Und weil das Backend dabei
    // über andere Schichten entscheidet, wirkt er auf die ganze Maske (#19444).
    const segmentWritePending = useSelector(selectSegmentWritePending);

    const valueLabels = availableSegments.reduce((acc, {value, labelI18n}) => {
        acc[value] = t(labelI18n);
        return acc;
    }, {});
    const valuesTranslated = availableSegments.map(({value}) => {
        return {value, label: valueLabels[value]};
    });

    return (
        <ParameterFieldContainer
            id={"input_segment"}
            label={label}
            showDescription={true}
            parameter={"segment"}
            parameterType={"AVM"}
            noReset={!hasOtherSegmentSource}
            valueLabels={valueLabels}
            noInputGroup={true}
            renderControl={({className, onChange, activeParameterType, hasConflict, value, disabled}) => {
                const color = getResourceColor(activeParameterType, hasConflict);
                const locked = disabled || segmentWritePending;

                return (
                    <>
                        <Dropdown
                            id={"segment"}
                            // Im Konflikt ist noch nichts gewählt, gerechnet wird aber mit dem
                            // effektiven Segment -- das steht deshalb als Platzhalter da (#19444).
                            // Mit ihm als Wert ließe er sich nicht bestätigen: PrimeReact meldet für
                            // den bereits markierten Eintrag kein onChange, der Konflikt bliebe stehen.
                            value={hasConflict ? null : value}
                            placeholder={hasConflict ? valueLabels[value] : undefined}
                            // Die Vorlage sticht in PrimeReact den placeholder (createLabel), der
                            // Platzhaltertext muss deshalb hier heraus und nicht aus der Prop kommen.
                            valueTemplate={(option) => (
                                <span className={`text-${color}`}>
                                    {option?.label ?? (hasConflict ? valueLabels[value] : "")}
                                </span>
                            )}
                            className={`w-full segment border-${hasConflict ? color : ""}`}
                            disabled={locked}
                            loading={segmentWritePending}
                            pt={{
                                button: {
                                    className: `p-button-sm ${className}`,
                                },
                                input: {
                                    className: hasConflict ? `text-${color}` : "",
                                },
                            }}
                            options={valuesTranslated}
                            optionLabel="label"
                            onChange={onChange}
                            scrollHeight="300px"
                            data-pr-position="top"
                        />
                        {hasConflict && (
                            <SegmentConflictOptions valueLabels={valueLabels} disabled={locked} onChange={onChange} />
                        )}
                    </>
                );
            }}
        />
    );
}
